// Public Route Component for authentication pages (sign-in, sign-up)
// Redirects authenticated users to appropriate dashboard/profile pages

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { LoadingSpinner } from '../ui';
import { validateReturnToPath } from '../../lib/utils/navigation';

interface PublicRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * The OIDC sign-in continuation. A user heading here is part-way through
 * signing in to a third-party relying party, and the onboarding diverts below
 * must not interrupt that — see the exemption.
 */
const OIDC_CONTINUE_PATH = '/oidc/continue';

export function PublicRoute({ children, redirectTo }: PublicRouteProps) {
  const { user, isLoading, isEmailVerified, profileCompleted } = useAuth();
  const location = useLocation();

  // Show loading state while authentication is being determined
  if (isLoading) {
    return <LoadingSpinner fullScreen message="Checking authentication..." />;
  }

  // If user is authenticated, redirect based on their status
  if (user) {
    // Check if there's a return URL from the location state. Keep the query
    // string: flows like /oidc/continue?request_id=... carry one-time state
    // in it.
    const state = location.state as { returnTo?: Location } | null;

    // Rebuilt defensively, because the two halves are used for two different
    // decisions and must not be able to disagree: the exemption below is
    // granted on the PATHNAME, while the navigation uses pathname + search.
    //
    // A `search` that is not actually a search string breaks that equivalence.
    // `{ pathname: '/oidc/continue', search: '/../../dashboard' }` satisfies
    // the exemption and then navigates somewhere else entirely, once the
    // router resolves the traversal. Requiring a well-formed search — absent,
    // empty, or leading `?` — makes the thing judged and the thing done the
    // same thing again.
    //
    // `location.state` is `unknown` at runtime whatever the cast above says,
    // so the pathname is type-checked rather than trusted.
    const returnToPathname =
      typeof state?.returnTo?.pathname === 'string'
        ? state.returnTo.pathname
        : undefined;
    const returnToSearch = state?.returnTo?.search;
    const searchIsWellFormed =
      returnToSearch === undefined ||
      returnToSearch === '' ||
      (typeof returnToSearch === 'string' && returnToSearch.startsWith('?'));
    const rawReturnTo =
      returnToPathname !== undefined && searchIsWellFormed
        ? `${returnToPathname}${returnToSearch ?? ''}`
        : undefined;

    // Validated rather than trusted. This component previously passed the
    // stored value straight to <Navigate>; it now rejects anything that is not
    // a relative in-app path, which changes behaviour for NON-OIDC
    // destinations too — a malformed returnTo now falls back to /dashboard
    // instead of being handed to the router. #1831 owns the wider hardening
    // (this validator does not reject a leading `/\`); the exemption below is
    // the part that must not be fooled.
    const returnTo = validateReturnToPath(rawReturnTo);

    // Use custom redirect path if provided
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    // Is this user part-way through signing in to a relying party?
    //
    // Matched on the PATHNAME alone, by strict equality. The combined
    // `rawReturnTo` always carries `?request_id=...`, so comparing that would
    // never match and the exemption would silently do nothing — the failure
    // mode being that the whole change appears to work while the inbox round
    // trip quietly survives. A prefix or substring match would instead be an
    // open-redirect lever. Requiring `returnTo !== null` means a value the
    // validator rejects cannot buy an exemption.
    const isOidcContinuation =
      returnTo !== null && returnToPathname === OIDC_CONTINUE_PATH;

    // PRD Section 5.2: Onboarding flow logic
    // Forward returnTo state through onboarding steps so users reach their
    // original destination after completing email verification and profile setup
    //
    // Both diverts are exempt for the OIDC continuation, and it has to be both.
    // /complete-profile is itself wrapped in `requireEmailVerified`
    // (router.tsx), so exempting only the verify-email divert would bounce the
    // user straight back to /verify-email and reinstate the inbox round trip
    // this change exists to remove — silently, and only for users who happen to
    // have no display name.
    //
    // Client-agnostic by construction: this keys on the destination path, and
    // never learns which relying party the request belongs to. That is
    // deliberate — the page receives an opaque request_id precisely so a code
    // cannot be pivoted to another client.
    if (!isOidcContinuation) {
      if (!isEmailVerified) {
        return <Navigate to="/verify-email" replace state={state} />;
      }

      if (!profileCompleted) {
        return <Navigate to="/complete-profile" replace state={state} />;
      }
    }

    // Return to original destination or default to dashboard
    const destination = returnTo || '/dashboard';
    return <Navigate to={destination} replace />;
  }

  // User is not authenticated, show public content
  return <>{children}</>;
}
